<template>
  <div
    class="flex flex-col items-center gap-2 border-2 border-gray-100 rounded-xl p-4 sm:p-6 cursor-pointer relative"
    :class="[selected ? 'border-primary-600' : 'border-gray-100']"
  >
    <div v-if="isHidden" class="absolute top-1 px-2 text-xs text-red-500">
      Hidden plan. Visible only for admins
    </div>
    <div
      v-if="isUsedBefore"
      class="absolute bottom-1 px-2 text-xs text-red-500"
    >
      This plan has already been used and is visible only to admins
    </div>
    <div class="flex flex-col items-center">
      <div class="text-lg font-bold">
        {{ title }}
      </div>
      <div v-if="description" class="text-gray-300">
        {{ description }}
      </div>
    </div>
    <div class="flex gap-1 flex-wrap justify-center">
      <div class="whitespace-nowrap font-semibold">
        {{
          price
            ? formatPrice(
                price.amount / 100,
                price.currency,
                languageMap[locale]
              )
            : t('subscriptions.free')
        }}
      </div>
      <div v-if="priceDescription" class="text-gray-300 whitespace-nowrap">
        ({{ priceDescription }})
      </div>
    </div>
    <div class="text-gray-300 italic font-semibold">
      {{ $t('tickets.vat') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { PlanPeriodUnit, type PlanPrice } from '~/models/subscriptions'
import { useOrders } from '~/stores/orders'
import { languageMap } from '~/models/locale'

const { formatPrice } = useOrders()
const { t, locale } = useI18n()

interface PropsInterface {
  selected: boolean
  title: string
  description: string
  price?: PlanPrice | null
  period: number
  periodUnit: PlanPeriodUnit
  isHidden?: boolean
  isUsedBefore?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  selected: false,
  price: null,
})

// subscription prices are in cents, so we have to divide by 100
const priceDescription = computed(() => {
  if (!props.price || props.periodUnit !== PlanPeriodUnit.Month) {
    return ''
  }

  return props.period > 1
    ? `${formatPrice(
        props.price.amount / props.period / 100,
        props.price.currency,
        languageMap[locale.value]
      )} / ${t('subscriptions.month')}`
    : ''
})
</script>
